import { deriveEcosystem, ago } from './ecosystemState';

// Derive the command view's state from the command feed
// (GET /apps/:orgId/:appId/command on nventr.io, GET /dev/ops/ecosystem on a
// kit instance — both are the ecosystem feed plus extra blocks). Everything
// the ecosystem scene already animates comes from deriveEcosystem; this adds
// the instrument channels: cost (arc fill / comet speed), uptime ticks,
// usage skyline bars, deploy beads, finding chips, memory gauge.

const fmtUsd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const SEV = ['critical', 'high', 'medium', 'low', 'info'];

export function deriveCommand(data) {
  const eco = deriveEcosystem(data);
  const d = data || {};
  const c = d.costs?.current || d.costs || null; // nventr.io: { current, prior }; kit: flat
  const hasCost = !!c && Number.isFinite(Number(c.mtdUsd));
  const limitUsd = Number(d.limits?.app) || null;
  const mtd = hasCost ? Number(c.mtdUsd) || 0 : 0;
  const projected = hasCost ? Number(c.projectedUsd) || 0 : 0;
  const burn = hasCost ? Number(c.burnPerDayUsd) || 0 : 0;
  // Arc fill: against the limit when one exists, else against the projection
  // (so the arc always means "how far through what this month will cost").
  const denom = limitUsd || projected || mtd || 1;
  const cost = {
    available: hasCost,
    mtdUsd: hasCost ? mtd : null,
    burnPerDayUsd: hasCost ? burn : null,
    projectedUsd: hasCost ? projected : null,
    priorUsd: d.costs?.prior?.mtdUsd ?? null,
    priorPeriod: d.costs?.priorPeriod || null,
    limitUsd,
    ratio: hasCost ? mtd / denom : 0,
    ghost: hasCost ? projected / denom : null,
    ticks: limitUsd ? [0.5, 0.8, 1] : [],
    level: !hasCost ? 'off' : limitUsd && mtd >= limitUsd ? 'down' : limitUsd && (mtd >= limitUsd * 0.8 || projected > limitUsd) ? 'warn' : 'ok',
    // Comet: one lap per (24h / burn-normalised) — map $0..$20/day → 0..0.35 laps/s.
    cometSpeed: hasCost && burn > 0 ? Math.min(0.35, 0.03 + burn / 60) : 0,
    infra: c?.infra || null,
    model: c?.model || null,
    dev: c?.dev || null,
    byUser: c?.byUser || {},
    pulledAt: c?.infra?.pulledAt || c?.pulledAt || null,
    text: hasCost ? `${fmtUsd(mtd)} mtd · ${fmtUsd(burn)}/day · ${fmtUsd(projected)} projected${limitUsd ? ` · limit ${fmtUsd(limitUsd)}` : ''}` : '',
  };

  const h = d.health || {};
  const health = {
    days: h.days || [],
    ticks: (h.days || []).map((x) => x.uptime),
    uptime14d: h.uptime14d ?? null,
    p50: h.p50 ?? null,
    p95: h.p95 ?? null,
    features: h.features || { total: 0, passing: 0, failing: [] },
    // Wobble amplitude for the monitor ring: 0 at ≤200ms, 1 at ≥2s p95.
    wobble: h.p95 != null ? Math.min(1, Math.max(0, (h.p95 - 200) / 1800)) : 0,
    incidentsOpen: (d.incidents || d.plane?.incidents || []).filter((i) => i.status === 'open').length,
  };

  const u = d.usage || null;
  const counters = u?.counters || {};
  const USAGE_KEYS = [
    ['tokens', 'tokens'], ['aiTurns', 'turns'], ['pipelineRuns', 'runs'], ['jobRuns', 'jobs'],
    ['agentConversations', 'convos'], ['devCents', 'dev ¢'], ['voiceMinutes', 'voice'],
  ];
  const topUsers = u?.byUser || [];
  const totalTok = Number(counters.tokens) || 0;
  const usage = {
    available: !!u,
    counters,
    bars: USAGE_KEYS.map(([k, label]) => ({
      key: k, label, value: Number(counters[k]) || 0,
      segments: k === 'tokens' && totalTok ? topUsers.slice(0, 3).map((x) => x.tokens / totalTok) : [],
    })).filter((b) => b.value > 0 || ['tokens', 'aiTurns', 'pipelineRuns'].includes(b.key)),
    topUsers,
    reportedAt: u?.reportedAt || null,
    lastActivityAt: u?.lastActivityAt || null,
    statement: d.statement || null,
  };

  const deploys = (d.recentDeploys || d.deploys || []).map((x) => ({
    env: x.env === 'uat' ? 'uat' : 'prod', status: x.status, at: x.at, sha: x.headSha || x.mergeSha || null,
    level: x.status === 'error' ? 'down' : x.status === 'done' ? 'ok' : 'warn',
  }));
  const beads = {
    prod: deploys.filter((x) => x.env === 'prod').slice(0, 5).reverse(),
    uat: deploys.filter((x) => x.env === 'uat').slice(0, 5).reverse(),
  };

  const counts = eco.security.counts || {};
  const security = {
    ...eco.security,
    bySeverity: SEV.map((k) => ({ key: k, count: Number(counts[k]) || 0 })),
    nextBaselineAt: d.scanSchedule?.nextBaselineAt || null,
    assurance: !!(d.instance || d.plane?.instance)?.assurance,
  };

  const host = d.host || null;
  const memory = {
    available: !!host?.load,
    memMb: host?.load?.memMb ?? null,
    cpuPct: host?.load?.cpuPct ?? null,
    share: host?.memShare ?? null,
    hostId: host?.id || null,
    hostVitals: host?.vitals || null,
  };

  const lic = d.license || null;
  const inst = d.instance || d.plane?.instance || {}; // kit feed nests it under plane
  const identity = {
    name: inst.name || eco.core.name,
    status: inst.status || eco.core.status,
    plan: d.plan || inst.plan || null,
    url: inst.url || d.plane?.instance?.url || null,
    kitRef: inst.kitRef || null,
    version: inst.version || null,
    pinnedKitRef: d.pinnedKitRef || null,
    kitDrift: !!(inst.kitRef && d.pinnedKitRef && inst.kitRef !== d.pinnedKitRef),
    prepaidUntil: d.prepaidUntil || null,
    license: lic ? { status: lic.status || lic.state, lastSeenAt: lic.lastSeenAt || null, termEnd: lic.termEnd || lic.graceUntil || null } : (lic === null && d.license !== undefined ? null : eco.plane.license ? { status: eco.plane.license } : null),
    period: d.period || null,
    assurance: !!inst.assurance,
  };

  // Raw records the stage cards read (see commandInspector): the scene
  // animates levels, the cards need the words behind them.
  const planeSrc = d.plane && !d.plane.unreachable ? d.plane : d;
  const incidents = (planeSrc?.incidents || d.incidents || []).map((i) => ({ id: i.id, kind: i.kind || null, status: i.status, reason: i.reason || '', openedAt: i.openedAt || 0, closedAt: i.closedAt || null }));
  const servicesAt = d.servicesAt || planeSrc?.servicesAt || null;
  const provisioning = planeSrc?.provisioning || d.provisioning || null;
  const updates = planeSrc?.updates || {};
  const twinSrc = planeSrc?.twin || null;
  const prodSrc = planeSrc?.prod || null;

  return { ...eco, cost, health, usage, beads, security, memory, identity, deploys, incidents, servicesAt, provisioning, updates: { versionBehind: !!updates.versionBehind, latestKitVersion: updates.latestKitVersion || null, planeSha: updates.planeSha || null }, twinSrc, prodSrc };
}

// ---------------------------------------------------------------------------
// Stage cards. `commandInspector(state, telemetry, sel)` builds the DOM card
// for a selection descriptor `{ kind, id }` emitted by CommandScene /
// EcosystemScene — rebuilt from the CURRENT state on every render so a
// pinned card is live. Kinds: core · service (name) · monitor · security ·
// twin (prod|uat) · edge (host) · plane · cost · usage · memory · flow
// (from>to) · anomaly (id). `sel == null` → the core card (default).
// Card shape matches CommandView: { kind, id, title, subtitle, tone, rows,
// sparks, sections, issues, open }.

const fmtN = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v) >= 1e6 ? `${(Number(v) / 1e6).toFixed(1)}M` : Number(v) >= 1e3 ? `${(Number(v) / 1e3).toFixed(1)}k` : String(Math.round(Number(v))));
const fmtMs = (v) => (v == null ? '—' : `${Math.round(v)} ms`);
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(v >= 0.999 ? 2 : 1)}%`);
const shaOf = (s) => (s ? String(s).slice(0, 7) : '—');
const toneOf = (level) => (level === 'warn' ? 'warn' : level === 'down' ? 'down' : level === 'ok' ? 'ok' : 'off');
const last = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null);

function trafficSparks(t, slug, { tokens = false } = {}) {
  const s = t?.series?.[slug];
  if (!s?.req?.length) return [];
  const out = [
    { label: 'requests / min', values: s.req.slice(-24), color: 'ok', last: `${fmtN(last(s.req))}/min` },
    { label: '5xx', values: (s.s5xx || []).slice(-24), color: (last(s.s5xx) || 0) > 0 ? 'down' : 'line', last: fmtN(last(s.s5xx) || 0) },
    { label: 'p95 latency', values: (s.p95 || []).slice(-24), color: (last(s.p95) || 0) > 1500 ? 'warn' : 'line', last: fmtMs(last(s.p95)) },
  ];
  if (tokens && s.tokens?.some((v) => v > 0)) out.push({ label: 'tokens', values: s.tokens.slice(-24), color: 'ok', last: fmtN(last(s.tokens)) });
  return out;
}

function flowSections(t, slug) {
  const flows = t?.flows || [];
  const out = flows.filter((f) => f.from === slug); const inn = flows.filter((f) => f.to === slug);
  const secs = [];
  if (out.length) secs.push({ title: 'flows out', rows: out.map((f) => [`→ ${f.to}`, `${fmtN(f.ratePerMin)}/min · ${f.kind}`]) });
  if (inn.length) secs.push({ title: 'flows in', rows: inn.map((f) => [`← ${f.from}`, `${fmtN(f.ratePerMin)}/min · ${f.kind}`]) });
  const rt = t?.runtime?.[slug];
  if (rt?.totals) secs.push({ title: 'runtime', rows: [['queued', fmtN(rt.totals.queued)], ['running', fmtN(rt.totals.running)], ['stale', fmtN(rt.totals.stale)]] });
  const an = (t?.anomalies || []).filter((a) => a.slug === slug && a.closedAt == null);
  if (an.length) secs.push({ title: `open anomalies · ${an.length}`, dots: an.slice(0, 5).map((a) => ({ level: a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn', text: `${a.metric} ${a.kind} · z ${Number(a.z).toFixed(1)}` })) });
  return secs;
}

function coreIssues(s) {
  const issues = [];
  const open = s.incidents.filter((i) => i.status === 'open');
  if (s.identity.status && s.identity.status !== 'live' && s.identity.status !== 'running') issues.push({ kind: 'status', level: s.identity.status === 'provisioning' ? 'busy' : 'down', text: `instance ${s.identity.status}${s.provisioning?.stage && s.provisioning.status !== 'done' ? ` · ${s.provisioning.stage}` : ''}` });
  if (['blocked', 'suspended', 'revoked'].includes(s.identity.license?.status)) issues.push({ kind: 'license', level: 'down', text: `license ${s.identity.license.status}` });
  else if (s.identity.license?.status === 'grace') issues.push({ kind: 'license', level: 'warn', text: 'license in grace' });
  if (!s.plane.reachable && s.plane.level !== 'off') issues.push({ kind: 'plane', level: 'warn', text: 'control plane unreachable' });
  if (open.length) issues.push({ kind: 'incident', level: open.some((i) => i.kind !== 'anomaly') ? 'down' : 'warn', text: `${open.length} open incident${open.length === 1 ? '' : 's'} · ${open[0].reason || open[0].kind || 'probe failed'}` });
  else if (s.monitor.level === 'warn') issues.push({ kind: 'probe', level: 'warn', text: 'monitor: probe failing' });
  else if (s.monitor.level === 'down') issues.push({ kind: 'probe', level: 'down', text: 'monitor: down' });
  const down = s.services.filter((x) => x.kind === 'service' && x.level === 'down');
  if (down.length) issues.push({ kind: 'service', level: 'down', text: `${down.length} service${down.length > 1 ? 's' : ''} down · ${down.map((x) => x.name).slice(0, 3).join(', ')}` });
  if (s.twin.deployRunning) issues.push({ kind: 'deploy', level: 'busy', text: `deploy running → ${s.twin.deployRunning}` });
  if (s.security.scanning) issues.push({ kind: 'scanning', level: 'busy', text: 'security scan running' });
  else if ((s.security.counts.critical || 0) + (s.security.counts.high || 0) > 0) issues.push({ kind: 'scan', level: s.security.counts.critical ? 'down' : 'warn', text: `scan · ${s.security.counts.critical || 0} critical · ${s.security.counts.high || 0} high` });
  const tlsMin = s.edge.reduce((m, x) => (x.tlsDaysLeft != null ? Math.min(m, x.tlsDaysLeft) : m), 99);
  if (tlsMin < 14) issues.push({ kind: 'tls', level: tlsMin < 3 ? 'down' : 'warn', text: `TLS ${tlsMin}d left` });
  if (s.updates.versionBehind) issues.push({ kind: 'version', level: 'warn', text: `kit update available${s.updates.latestKitVersion ? ` · ${s.updates.latestKitVersion}` : ''}` });
  if (s.identity.kitDrift) issues.push({ kind: 'drift', level: 'warn', text: `kit ${s.identity.kitRef} · pinned ${s.identity.pinnedKitRef}` });
  if (s.cost.level === 'warn' || s.cost.level === 'down') issues.push({ kind: 'cost', level: s.cost.level, text: s.cost.level === 'down' ? 'over the monthly cost limit' : 'projected over the cost limit' });
  if (s.health.features.failing.length) issues.push({ kind: 'feature', level: 'warn', text: `${s.health.features.failing.length} feature check${s.health.features.failing.length > 1 ? 's' : ''} failing · ${s.health.features.failing.slice(0, 2).join(', ')}` });
  return issues;
}

function coreCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const live = t?.live?.[slug];
  const pc = t?.planeCalls?.[slug];
  const id = s.identity;
  const issues = coreIssues(s);
  const rows = [
    ['status', `${id.status || '—'}${s.monitor.level !== 'off' ? ` · monitor ${s.monitor.level}` : ''}`],
    ['probe', s.monitor.lastCheckAt ? `${fmtMs(s.monitor.latencyMs)} · ${ago(s.monitor.lastCheckAt)}` : 'no probe yet'],
    ['uptime 14d', `${pct(s.health.uptime14d)} · p95 ${fmtMs(s.health.p95)}`],
    live ? ['traffic now', `${fmtN(live.req)}/min · ${fmtN(live.s5xx)} 5xx · p95 ${fmtMs(live.p95)}`] : null,
    pc ? ['plane calls', `${fmtN(pc.ratePerMin)}/min`] : null,
    s.cost.available ? ['cost mtd / proj', `${fmtUsd(s.cost.mtdUsd)} / ${fmtUsd(s.cost.projectedUsd)}${s.cost.limitUsd ? ` · limit ${fmtUsd(s.cost.limitUsd)}` : ''}`] : null,
    s.cost.available ? ['burn / day', fmtUsd(s.cost.burnPerDayUsd)] : null,
    s.usage.available ? ['usage', `${fmtN(s.usage.counters.tokens)} tokens · ${fmtN(s.usage.counters.aiTurns)} turns · ${fmtN(s.usage.counters.pipelineRuns)} runs`] : null,
    ['services', s.services.length ? `${s.services.filter((x) => x.level === 'ok').length}/${s.services.length} up${s.servicesAt ? ` · ${ago(s.servicesAt)}` : ''}` : 'no report'],
    ['kit', `${id.kitRef || '—'}${id.version ? ` · v${id.version}` : ''}${id.kitDrift ? ` · pinned ${id.pinnedKitRef}` : ''}`],
    id.license ? ['license', `${id.license.status}${id.license.lastSeenAt ? ` · seen ${ago(id.license.lastSeenAt)}` : ''}`] : null,
    s.usage.lastActivityAt ? ['last activity', ago(s.usage.lastActivityAt)] : null,
    id.url ? ['url', id.url.replace(/^https?:\/\//, '')] : null,
  ].filter(Boolean);
  const sections = [];
  if (s.services.length) sections.push({ title: `services · ${s.services.filter((x) => x.level === 'ok').length}/${s.services.length} up`, dots: s.services.slice(0, 12).map((x) => ({ level: x.level === 'off' ? 'idle' : x.level, text: `${x.name}${x.status && x.status !== 'running' ? ` · ${x.status}` : ''}` })) });
  const open = s.incidents.filter((i) => i.status === 'open');
  if (open.length) sections.push({ title: `open incidents · ${open.length}`, dots: open.slice(0, 5).map((i) => ({ level: i.kind === 'anomaly' ? 'warn' : 'down', text: `${i.reason || i.kind || 'probe failed'} · ${ago(i.openedAt)}` })) });
  sections.push(...flowSections(t, slug));
  if (s.provisioning && s.provisioning.status !== 'done') sections.push({ title: 'provisioning', rows: [['job', `${s.provisioning.kind || 'provision'} · ${s.provisioning.status}`], ['stage', s.provisioning.stage || '—'], ['at', ago(s.provisioning.at)]] });
  return {
    kind: 'core', id: 'core', title: id.name || slug || 'instance', tone: toneOf(s.overall),
    subtitle: [slug, id.plan, s.viewer?.role === 'uat' ? 'you are on the UAT twin' : null].filter(Boolean).join(' · '),
    rows, sparks: trafficSparks(t, slug, { tokens: true }), sections, issues, open: 'ap-identity',
  };
}

function serviceCard(s, name) {
  const x = s.services.find((v) => v.name === name);
  if (!x) return null;
  return {
    kind: 'service', id: name, title: name, subtitle: x.kind === 'runner' ? 'runner · finishes and exits' : 'container', tone: x.level === 'off' ? 'idle' : toneOf(x.level),
    rows: [['state', x.status || '—'], ['health', x.level === 'ok' ? 'healthy' : x.level === 'warn' ? 'starting' : x.level === 'down' ? 'down' : 'stopped'], s.servicesAt ? ['reported', ago(s.servicesAt)] : null].filter(Boolean),
    sections: [{ title: `all services · ${s.services.filter((v) => v.level === 'ok').length}/${s.services.length} up`, dots: s.services.slice(0, 12).map((v) => ({ level: v.level === 'off' ? 'idle' : v.level, text: v.name })) }],
    issues: x.level === 'down' ? [{ kind: 'service', level: 'down', text: `${name} is not running` }] : [], open: 'ap-services',
  };
}

function monitorCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const m = s.monitor; const h = s.health;
  const ticks = h.days.filter((d) => d.uptime != null);
  const sparks = [];
  if (ticks.length > 1) sparks.push({ label: 'uptime · 14d', values: h.days.map((d) => (d.uptime == null ? 0 : d.uptime * 100)), color: ticks.some((d) => d.uptime < 0.99) ? 'warn' : 'ok', last: pct(last(ticks)?.uptime) });
  const p95s = h.days.map((d) => d.p95 || 0);
  if (p95s.some((v) => v > 0)) sparks.push({ label: 'p95 · daily', values: p95s, color: 'line', last: fmtMs(last(p95s)) });
  const ser = t?.series?.[slug];
  if (ser?.p95?.length) sparks.push({ label: `p95 · ${t.window?.label || 'live'}`, values: ser.p95.slice(-24), color: (last(ser.p95) || 0) > 1500 ? 'warn' : 'line', last: fmtMs(last(ser.p95)) });
  const open = s.incidents.filter((i) => i.status === 'open'); const closed = s.incidents.filter((i) => i.status !== 'open');
  const sections = [];
  if (open.length || closed.length) sections.push({ title: `incidents · ${open.length} open`, dots: [...open, ...closed].slice(0, 6).map((i) => ({ level: i.status === 'open' ? (i.kind === 'anomaly' ? 'warn' : 'down') : 'ok', text: `${i.status === 'open' ? 'open' : 'closed'} · ${i.reason || i.kind || 'probe failed'} · ${ago(i.closedAt || i.openedAt)}` })) });
  if (h.features.total) sections.push({ title: `feature checks · ${h.features.passing}/${h.features.total} passing`, dots: h.features.failing.length ? h.features.failing.map((f) => ({ level: 'warn', text: `${f} failing` })) : [{ level: 'ok', text: 'all passing' }] });
  return {
    kind: 'monitor', id: 'monitor', title: 'uptime monitor', subtitle: m.level === 'off' ? 'no probe yet' : `probe every minute · ${m.level}`, tone: toneOf(m.level),
    rows: [['last probe', m.lastCheckAt ? `${fmtMs(m.latencyMs)} · ${ago(m.lastCheckAt)}` : '—'], ['uptime 14d', pct(h.uptime14d)], ['p50 / p95', `${fmtMs(h.p50)} / ${fmtMs(h.p95)}`], ['TLS', m.tlsDaysLeft != null ? `${m.tlsDaysLeft}d left` : '—'], ['incidents open', String(open.length)]],
    sparks, sections, issues: coreIssues(s).filter((i) => ['probe', 'incident', 'tls', 'feature'].includes(i.kind)), open: 'ap-health',
  };
}

function securityCard(s) {
  const c = s.security.counts || {};
  const bars = s.security.bySeverity.filter((x) => x.count > 0).map((x) => ({ label: x.key, value: x.count }));
  return {
    kind: 'security', id: 'security', title: 'security', subtitle: s.security.scanning ? 'scan running now' : s.security.lastScanAt ? `last scan ${ago(s.security.lastScanAt)}` : 'never scanned', tone: s.security.scanning ? 'busy' : toneOf(s.security.level),
    rows: [['critical / high', `${c.critical || 0} / ${c.high || 0}`], ['medium / low', `${c.medium || 0} / ${c.low || 0}`], s.security.nextBaselineAt ? ['next baseline', new Date(s.security.nextBaselineAt).toLocaleDateString()] : null, ['assurance', s.security.assurance ? 'on' : 'off']].filter(Boolean),
    sections: bars.length ? [{ title: 'findings by severity', bars }] : [],
    issues: coreIssues(s).filter((i) => i.kind === 'scan' || i.kind === 'scanning'), open: 'ap-security',
  };
}

function twinCard(s, env) {
  const uat = env === 'uat';
  const src = uat ? s.twinSrc : s.prodSrc;
  if (uat && !s.twin.exists) {
    return { kind: 'twin', id: 'uat', title: 'UAT twin', subtitle: 'not added', tone: 'off', rows: [['status', 'no twin for this app']], issues: [], open: 'ap-envs' };
  }
  const lastDeploy = src?.lastDeploy || null;
  const list = s.deploys.filter((d) => d.env === env).slice(0, 6);
  const level = uat ? s.twin.level : s.twin.prodLevel;
  return {
    kind: 'twin', id: env, title: uat ? 'UAT twin' : 'production', subtitle: uat ? [src?.slug, src?.status].filter(Boolean).join(' · ') : (s.identity.url || '').replace(/^https?:\/\//, ''), tone: s.twin.deployRunning === env ? 'busy' : toneOf(level),
    rows: [
      uat ? ['status', src?.status || '—'] : ['status', s.identity.status || '—'],
      uat && src?.monitor ? ['probe', `${src.monitor.state || '—'}${src.monitor.lastCheckAt ? ` · ${ago(src.monitor.lastCheckAt)}` : ''}`] : null,
      ['last deploy', lastDeploy ? `${lastDeploy.status} · ${shaOf(lastDeploy.headSha || lastDeploy.mergeSha)} · ${ago(lastDeploy.at)}` : '—'],
      s.twin.deployRunning === env ? ['deploying', 'now'] : null,
      uat && src?.url ? ['url', src.url.replace(/^https?:\/\//, '')] : null,
    ].filter(Boolean),
    sections: list.length ? [{ title: `recent deploys · ${env}`, dots: list.map((d) => ({ level: d.level, text: `${d.status} · ${shaOf(d.sha)} · ${ago(d.at)}` })) }] : [],
    issues: lastDeploy?.status === 'error' ? [{ kind: 'deploy', level: 'warn', text: `last ${env} deploy failed` }] : [], open: 'ap-envs',
  };
}

function edgeCard(s, host) {
  const d = s.edge.find((x) => x.host === host);
  if (!d) return null;
  return {
    kind: 'edge', id: host, title: host, subtitle: `${d.label} domain`, tone: toneOf(d.level),
    rows: [['TLS', d.tlsDaysLeft != null ? `${d.tlsDaysLeft}d left` : 'unknown'], ['serves', d.label]],
    sections: [{ title: `edge · ${s.edge.length} domain${s.edge.length === 1 ? '' : 's'}`, dots: s.edge.map((x) => ({ level: x.level, text: `${x.host} · ${x.label}${x.tlsDaysLeft != null ? ` · TLS ${x.tlsDaysLeft}d` : ''}` })) }],
    issues: d.level !== 'ok' ? [{ kind: 'tls', level: d.level, text: `certificate expires in ${d.tlsDaysLeft}d` }] : [], open: 'ap-health',
  };
}

function planeCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const pc = t?.planeCalls?.[slug];
  return {
    kind: 'plane', id: 'plane', title: 'nventr control plane', subtitle: s.plane.reachable ? 'reachable · probes every minute' : 'unreachable', tone: toneOf(s.plane.level),
    rows: [['license', s.identity.license ? `${s.identity.license.status}${s.identity.license.termEnd ? ` · to ${new Date(s.identity.license.termEnd).toLocaleDateString()}` : ''}` : s.plane.license || '—'], ['kit update', s.updates.versionBehind ? `available · ${s.updates.latestKitVersion || ''}` : 'up to date'], ['pinned kit', s.identity.pinnedKitRef || '—'], s.updates.planeSha ? ['plane build', shaOf(s.updates.planeSha)] : null, pc ? ['plane calls', `${fmtN(pc.ratePerMin)}/min${pc.lastAt ? ` · ${ago(pc.lastAt)}` : ''}`] : null, ['last probe', s.monitor.lastCheckAt ? ago(s.monitor.lastCheckAt) : '—']].filter(Boolean),
    sections: s.events.length ? [{ title: 'recent activity', dots: s.events.slice(0, 5).map((e) => ({ level: e.level || 'ok', text: `${e.text} · ${ago(e.at)}` })) }] : [],
    issues: coreIssues(s).filter((i) => ['plane', 'license', 'version', 'drift'].includes(i.kind)), open: 'ap-identity',
  };
}

function costCard(s) {
  const c = s.cost;
  if (!c.available) return { kind: 'cost', id: 'cost', title: 'cost', subtitle: 'not visible at this seat', tone: 'off', rows: [], issues: [], open: 'ap-costs' };
  const parts = [['infra', c.infra?.mtdUsd ?? c.infra?.usd], ['model', c.model?.mtdUsd ?? c.model?.usd], ['dev', c.dev?.mtdUsd ?? c.dev?.usd]].filter(([, v]) => Number.isFinite(Number(v)) && Number(v) > 0).map(([k, v]) => ({ label: k, value: Number(v), text: fmtUsd(v) }));
  const users = Object.entries(c.byUser || {}).map(([k, v]) => ({ label: k, value: Number(v?.usd ?? v) || 0 })).filter((x) => x.value > 0).sort((a, b) => b.value - a.value).slice(0, 5).map((x) => ({ ...x, text: fmtUsd(x.value) }));
  return {
    kind: 'cost', id: 'cost', title: `cost · ${s.identity.period || 'this month'}`, subtitle: c.limitUsd ? `${Math.round(c.ratio * 100)}% of the $${c.limitUsd} limit` : 'no limit set · arc = share of projection', tone: toneOf(c.level),
    rows: [['month to date', fmtUsd(c.mtdUsd)], ['burn / day', fmtUsd(c.burnPerDayUsd)], ['projected', fmtUsd(c.projectedUsd)], c.limitUsd ? ['limit', fmtUsd(c.limitUsd)] : null, c.priorUsd != null ? [`prior · ${c.priorPeriod || ''}`, fmtUsd(c.priorUsd)] : null, c.pulledAt ? ['pulled', ago(c.pulledAt)] : null].filter(Boolean),
    sections: [parts.length ? { title: 'by kind', bars: parts } : null, users.length ? { title: 'by user', bars: users } : null].filter(Boolean),
    issues: coreIssues(s).filter((i) => i.kind === 'cost'), open: 'ap-costs',
  };
}

function usageCard(s, t) {
  const slug = s.viewer?.slug || s.core.name;
  const u = s.usage;
  if (!u.available) return { kind: 'usage', id: 'usage', title: 'usage', subtitle: 'no report this month', tone: 'off', rows: [], issues: [], open: 'ap-usage' };
  const ser = t?.series?.[slug];
  const sparks = [];
  if (ser?.tokens?.some((v) => v > 0)) sparks.push({ label: 'tokens', values: ser.tokens.slice(-24), color: 'ok', last: fmtN(last(ser.tokens)) });
  if (ser?.convos?.some((v) => v > 0)) sparks.push({ label: 'conversations', values: ser.convos.slice(-24), color: 'line', last: fmtN(last(ser.convos)) });
  const total = Number(u.counters.tokens) || 0;
  return {
    kind: 'usage', id: 'usage', title: `usage · ${s.identity.period || 'this month'}`, subtitle: u.reportedAt ? `reported ${ago(u.reportedAt)}` : '', tone: 'ok',
    rows: u.bars.map((b) => [b.label, fmtN(b.value)]).concat(u.statement ? [['statement', `${u.statement.lines} lines · $${(u.statement.subtotalCents / 100).toFixed(2)} · ${u.statement.status}`]] : []),
    sparks, sections: u.topUsers.length && total ? [{ title: 'top users · tokens', bars: u.topUsers.slice(0, 5).map((x) => ({ label: x.uid.slice(0, 10), value: x.tokens, text: `${fmtN(x.tokens)} · ${Math.round((x.tokens / total) * 100)}%` })) }] : [],
    issues: [], open: 'ap-usage',
  };
}

function memoryCard(s) {
  const m = s.memory;
  if (!m.available) return { kind: 'memory', id: 'memory', title: 'host memory', subtitle: 'no host sample', tone: 'off', rows: [], issues: [], open: 'ap-identity' };
  const v = m.hostVitals;
  return {
    kind: 'memory', id: 'memory', title: 'host memory', subtitle: m.hostId ? `host …${String(m.hostId).slice(-6)}` : '', tone: (m.share ?? 0) > 0.6 ? 'warn' : 'ok',
    rows: [['this stack', `${Math.round(m.memMb || 0)} MB`], ['share of sampled', m.share != null ? `${Math.round(m.share * 100)}%` : '—'], m.cpuPct != null ? ['cpu', `${Math.round(m.cpuPct)}%`] : null, v ? ['host', `${v.state || '—'}${v.memUsedPct != null ? ` · mem ${v.memUsedPct}%` : ''}${v.disk != null ? ` · disk ${v.disk}%` : ''}`] : null].filter(Boolean),
    sections: [], issues: (m.share ?? 0) > 0.6 ? [{ kind: 'memory', level: 'warn', text: 'holds most of the host’s sampled memory' }] : [], open: 'ap-identity',
  };
}

function flowCard(s, t, id) {
  const f = (t?.flows || []).find((x) => `${x.from}>${x.to}` === id);
  if (!f) return null;
  return { kind: 'flow', id, title: `${f.from} → ${f.to}`, subtitle: `${f.kind} flow · ${t.window?.label || ''}`, tone: 'ok', rows: [['rate', `${fmtN(f.ratePerMin)}/min`], ['calls', fmtN(f.n)], ['kind', f.kind]], issues: [], open: 'ap-traffic' };
}

function anomalyCard(s, t, id) {
  const a = (t?.anomalies || []).find((x) => x.id === id);
  if (!a) return null;
  const sev = a.severity === 'high' || a.severity === 'critical' ? 'down' : 'warn';
  return {
    kind: 'anomaly', id, title: `${a.metric} ${a.kind}`, subtitle: `${a.slug} · ${a.closedAt ? 'resolved' : 'open'}`, tone: a.closedAt ? 'ok' : sev,
    rows: [['value', fmtN(a.value)], ['baseline', fmtN(a.baseline)], ['z', Number(a.z).toFixed(1)], ['opened', ago(a.openedAt)], a.closedAt ? ['closed', ago(a.closedAt)] : null].filter(Boolean),
    issues: a.text ? [{ kind: 'anomaly', level: sev, text: a.text }] : [], open: 'ap-anomalies',
  };
}

export function commandInspector(s, t, sel) {
  if (!s) return null;
  if (!sel || sel.kind === 'core') return coreCard(s, t);
  if (sel.kind === 'service') return serviceCard(s, sel.id);
  if (sel.kind === 'monitor') return monitorCard(s, t);
  if (sel.kind === 'security') return securityCard(s);
  if (sel.kind === 'twin') return twinCard(s, sel.id);
  if (sel.kind === 'edge') return edgeCard(s, sel.id);
  if (sel.kind === 'plane') return planeCard(s, t);
  if (sel.kind === 'cost') return costCard(s);
  if (sel.kind === 'usage') return usageCard(s, t);
  if (sel.kind === 'memory') return memoryCard(s);
  if (sel.kind === 'flow') return flowCard(s, t, sel.id);
  if (sel.kind === 'anomaly') return anomalyCard(s, t, sel.id);
  return null;
}
